import { Directive, HostBinding, HostListener, inject, Input } from '@angular/core';
import { ToasterService } from 'src/core/services/toaster/toaster.service';

/**
 * Copies the given text to the clipboard when its host is clicked, and confirms it with a toast. While
 * the text is blank the host is left untouched: no hover affordance, and a click is not intercepted.
 *
 * Pass the value exactly as it is displayed (piped), not the raw model value — the user copies what they see.
 *
 * Deliberately sets no `title`/`tabindex` on the host: a native title would stack a second tooltip on
 * top of a `pTooltip` living on the same element or its ancestors. The hover look (`.cx-copyable`) is a
 * global rule in `styles.scss`, since an attribute directive cannot carry styles.
 */
@Directive({
  selector: '[appCopyOnClick]',
  standalone: true,
})
export class CopyOnClickDirective {
  private readonly toaster = inject(ToasterService);

  @Input() appCopyOnClick: string | null | undefined;

  @HostBinding('class.cx-copyable')
  get copyable(): boolean {
    return !!this.appCopyOnClick?.trim();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.copyable) {
      return;
    }

    // Lists react to row clicks (`base-list` emits `rowClick`); a copy must not also act as one.
    event.stopPropagation();

    // Started synchronously inside the click: the clipboard write needs the click's user activation.
    if (!navigator.clipboard) {
      this.toaster.error('::CopyToClipboardFailed');
      return;
    }

    void navigator.clipboard.writeText(this.appCopyOnClick!.trim()).then(
      () => this.toaster.success('::CopiedToClipboard'),
      () => this.toaster.error('::CopyToClipboardFailed'),
    );
  }
}
